/**
 * Theme configuration with design tokens
 * Based on DESIGN_TOKENS.md specification
 * Uses centralized design system tokens
 */

import { createTheme } from '@mui/material/styles';
import { tokens } from './design-system';

/**
 * Detect user preference for reduced motion
 * Used for accessibility (WCAG 2.1 AA)
 */
function getMotionPreference(): 'reduce' | 'normal' {
  if (typeof window === 'undefined') return 'normal';
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'reduce' : 'normal';
}

/**
 * Legacy export for backward compatibility
 * Use `tokens` from design-system instead for new code
 */
export const designTokens = {
  colors: {
    success: tokens.colors.success[500],
    warning: tokens.colors.warning[500],
    danger: tokens.colors.error[500],
    info: tokens.colors.info[500],
    neutral: tokens.colors.gray[500],
    
    // Semantic
    background: tokens.colors.semantic.background,
    surface: tokens.colors.semantic.surface,
    text: tokens.colors.semantic.text,
    textSecondary: tokens.colors.semantic.textSecondary,
    border: tokens.colors.semantic.border,
    
    // Gradients
    primary: tokens.colors.gradients.primary,
    accent: tokens.colors.gradients.accent,
    vibrant: tokens.colors.gradients.vibrant,
  },
  
  spacing: tokens.spacing.inset,
  
  typography: {
    fontFamily: tokens.typography.fontFamily.system,
    
    scale: {
      xs: tokens.typography.textStyles.small,
      sm: tokens.typography.textStyles.label,
      base: tokens.typography.textStyles.body,
      lg: tokens.typography.textStyles.heading4,
      xl: tokens.typography.textStyles.heading3,
      '2xl': tokens.typography.textStyles.heading2,
    },
  },
  
  motion: {
    // Check for reduced motion preference
    transitionDuration: getMotionPreference() === 'reduce' ? '0ms' : `${tokens.motion.duration.normal}ms`,
    easingFunction: tokens.motion.easing.easeInOut,
  },
  
  shadows: {
    sm: tokens.shadows.glass.sm,
    md: tokens.shadows.glass.md,
    lg: tokens.shadows.glass.lg,
  },
};

// Re-export design tokens for use throughout the app
export { tokens } from './design-system';


export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.colors.primary[500],
    },
    secondary: {
      main: tokens.colors.secondary[500],
    },
    success: {
      main: tokens.colors.success[500],
    },
    warning: {
      main: tokens.colors.warning[500],
    },
    error: {
      main: tokens.colors.error[500],
    },
    info: {
      main: tokens.colors.info[500],
    },
    background: {
      default: tokens.colors.semantic.background,
      paper: tokens.colors.semantic.surface,
    },
    text: {
      primary: tokens.colors.semantic.text,
      secondary: tokens.colors.semantic.textSecondary,
    },
    divider: tokens.colors.semantic.border,
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.system,
    h1: {
      fontSize: tokens.typography.fontSize['4xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h2: {
      fontSize: tokens.typography.fontSize['3xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h3: {
      fontSize: tokens.typography.fontSize['2xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h4: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h5: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h6: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body1: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.relaxed,
    },
    body2: {
      fontSize: tokens.typography.fontSize.md,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.relaxed,
    },
    caption: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: parseInt(tokens.radius.md),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0, 0, 0, 0.2)',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.colors.primary[400],
    },
    secondary: {
      main: tokens.colors.secondary[400],
    },
    success: {
      main: tokens.colors.success[400],
    },
    warning: {
      main: tokens.colors.warning[400],
    },
    error: {
      main: tokens.colors.error[400],
    },
    info: {
      main: tokens.colors.info[400],
    },
    background: {
      default: tokens.colors.dark.bg,
      paper: tokens.colors.dark.surface,
    },
    text: {
      primary: tokens.colors.dark.text,
      secondary: tokens.colors.gray[400],
      disabled: tokens.colors.gray[600],
    },
    divider: tokens.colors.dark.border,
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.system,
    h1: {
      fontSize: tokens.typography.fontSize['4xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
      color: tokens.colors.dark.text,
    },
    h2: {
      fontSize: tokens.typography.fontSize['3xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.dark.text,
    },
    h3: {
      fontSize: tokens.typography.fontSize['2xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.dark.text,
    },
    h4: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.dark.text,
    },
    h5: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.dark.text,
    },
    h6: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.dark.text,
    },
    body1: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.relaxed,
      color: tokens.colors.dark.text,
    },
    body2: {
      fontSize: tokens.typography.fontSize.base,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.relaxed,
      color: tokens.colors.gray[300],
    },
    caption: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
      color: tokens.colors.gray[400],
    },
  },
  spacing: 8,
  shape: {
    borderRadius: parseInt(tokens.radius.md),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
  },
});

